import { lazy, Suspense, useEffect, useState } from 'react'
import { Building2, LockKeyhole, MapPin, QrCode } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'
import { NameEditModal } from '@/components/admin/profile/NameEditModal'
import { PhoneVerifyModal } from '@/components/admin/profile/PhoneVerifyModal'
import KakaoPostcode from '@/components/common/KakaoPostcode'
import { centerApi } from '@/api/centerInfo'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { getApiErrorMessage } from '@/utils/apiError'
import { isLcOwner } from '@/utils/roleUtils'
import type { CenterUpdateRequest } from '@/types/center'

const LearningAccessQr = lazy(() => import('./LearningAccessQr'))

const MallAddressPage = lazy(() => import('@/pages/admin/mall/MallAddressPage'))

export default function CenterInfo() {
  const { t } = useTranslation(['admin', 'common'])
  const user = useAuthStore((state) => state.user)
  const applyUserUpdate = useAuthStore((state) => state.applyUserUpdate)
  const refreshTenant = useTenantStore((s) => s.initializeTenant)
  const qc = useQueryClient()
  const isOwner = isLcOwner(user ?? undefined)

  const { data } = useQuery({
    queryKey: ['lc-center-info'],
    queryFn: async () => (await centerApi.get()).data.data,
  })

  const [form, setForm] = useState<CenterUpdateRequest>({
    centerName: '', phoneNumber: '', postalCode: '', addressLine1: '', addressLine2: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [isNameModalOpen, setIsNameModalOpen] = useState(false)
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false)

  useEffect(() => {
    if (data) {
      setForm({
        centerName: data.centerName ?? '',
        phoneNumber: data.phoneNumber ?? '',
        postalCode: data.postalCode ?? '',
        addressLine1: data.addressLine1 ?? '',
        addressLine2: data.addressLine2 ?? '',
      })
    }
  }, [data])

  const onChange = (key: keyof CenterUpdateRequest, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const mutation = useMutation({
    mutationFn: (payload: CenterUpdateRequest) => centerApi.update(payload),
    onSuccess: () => {
      toast.success(t('center.saveSuccess'))
      qc.invalidateQueries({ queryKey: ['lc-center-info'] })
      void refreshTenant()
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, t) || t('center.saveError')),
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    if (!form.centerName.trim()) {
      toast.error(t('center.centerNameRequired'))
      return
    }
    mutation.mutate(form)
  }

  const refreshOwnerInfo = () => {
    void qc.invalidateQueries({ queryKey: ['lc-center-info'] })
  }

  const handleNameSaved = (newName: string) => {
    applyUserUpdate({ fullName: newName })
    refreshOwnerInfo()
  }

  const centerForm = (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>{t('center.basicInfoTitle')}</CardTitle>
        <CardDescription>{t('center.basicInfoDescription')}</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit}>
        <CardContent>
          <FieldGroup>
            <Field data-invalid={submitted && !form.centerName.trim()}>
              <FieldLabel htmlFor="center-name">{t('center.centerName')}</FieldLabel>
              <Input
                id="center-name"
                value={form.centerName}
                onChange={(e) => onChange('centerName', e.target.value)}
                aria-invalid={submitted && !form.centerName.trim()}
              />
            </Field>

            <FieldSet>
              <FieldLegend variant="label">{t('center.address')}</FieldLegend>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="center-postal-code" className="sr-only">
                    {t('center.postalCode')}
                  </FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="center-postal-code"
                      value={form.postalCode ?? ''}
                      readOnly
                      placeholder={t('center.postalCode')}
                      className="max-w-32"
                    />
                    <KakaoPostcode
                      label={t('center.searchAddress')}
                      onSelect={({ postalCode, addressBase }) =>
                        setForm((prev) => ({ ...prev, postalCode, addressLine1: addressBase }))
                      }
                    />
                  </div>
                </Field>
                <Field>
                  <FieldLabel htmlFor="center-address-line-1" className="sr-only">
                    {t('center.address')}
                  </FieldLabel>
                  <Input
                    id="center-address-line-1"
                    value={form.addressLine1 ?? ''}
                    readOnly
                    placeholder={t('center.address')}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="center-address-line-2" className="sr-only">
                    {t('center.addressDetail')}
                  </FieldLabel>
                  <Input
                    id="center-address-line-2"
                    value={form.addressLine2 ?? ''}
                    onChange={(e) => onChange('addressLine2', e.target.value)}
                    placeholder={t('center.addressDetail')}
                  />
                </Field>
              </FieldGroup>
            </FieldSet>

            <Field>
              <FieldLabel htmlFor="center-main-phone">{t('center.mainPhone')}</FieldLabel>
              <Input
                id="center-main-phone"
                value={form.phoneNumber ?? ''}
                onChange={(e) => onChange('phoneNumber', e.target.value)}
                inputMode="tel"
                autoComplete="tel"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel>{t('center.directorName')}</FieldLabel>
                <div className="flex min-h-8 items-center justify-between gap-2">
                  <output className="py-1 text-sm">
                    {data?.ownerName ?? t('center.notAssigned')}
                  </output>
                  {isOwner ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('center.directorNameEdit')}
                      onClick={() => setIsNameModalOpen(true)}
                    >
                      {t('profile.name.editButton')}
                    </Button>
                  ) : null}
                </div>
              </Field>
              <Field>
                <FieldLabel>{t('center.directorPhone')}</FieldLabel>
                <div className="flex min-h-8 items-center justify-between gap-2">
                  <output className="py-1 text-sm">
                    {data?.ownerPhoneNumber ?? t('center.notAssigned')}
                  </output>
                  {isOwner ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('center.directorPhoneEdit')}
                      onClick={() => setIsPhoneModalOpen(true)}
                    >
                      {data?.ownerPhoneNumber
                        ? t('profile.phone.changeButton')
                        : t('profile.phone.registerButton')}
                    </Button>
                  ) : null}
                </div>
              </Field>
            </div>
            <FieldDescription>
              {isOwner ? t('center.directorOwnerHint') : t('center.directorReadonlyHint')}
            </FieldDescription>
          </FieldGroup>
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? <Spinner data-icon="inline-start" /> : null}
            {mutation.isPending ? t('center.saving') : t('center.save')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('center.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('center.description')}</p>
      </header>

      <Tabs defaultValue="basic">
        <TabsList variant="line" aria-label={t('center.title')}>
          <TabsTrigger value="basic">
            <Building2 data-icon="inline-start" />
            {t('center.tabs.basic')}
          </TabsTrigger>
          <TabsTrigger value="addresses">
            <MapPin data-icon="inline-start" />
            {t('center.tabs.addresses')}
          </TabsTrigger>
          <TabsTrigger value="learning-qr">
            <QrCode data-icon="inline-start" />
            {t('center.learningQr.title')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="basic" className="pt-4">
          {centerForm}
        </TabsContent>
        <TabsContent value="addresses" className="pt-4">
          {isOwner ? (
            <Suspense fallback={<div className="flex min-h-40 items-center justify-center"><Spinner /></div>}>
              <MallAddressPage embedded />
            </Suspense>
          ) : (
            <Alert className="max-w-2xl">
              <LockKeyhole />
              <AlertTitle>{t('center.addressesOwnerOnlyTitle')}</AlertTitle>
              <AlertDescription>{t('center.addressesOwnerOnlyDescription')}</AlertDescription>
            </Alert>
          )}
        </TabsContent>
        <TabsContent value="learning-qr" className="pt-4">
          {data ? (
            <Suspense fallback={<Spinner />}>
              <LearningAccessQr centerName={data.centerName} />
            </Suspense>
          ) : <Spinner />}
        </TabsContent>
      </Tabs>

      {isOwner ? (
        <>
          <NameEditModal
            isOpen={isNameModalOpen}
            currentName={data?.ownerName ?? user?.fullName ?? ''}
            onClose={() => setIsNameModalOpen(false)}
            onSaved={handleNameSaved}
          />
          <PhoneVerifyModal
            isOpen={isPhoneModalOpen}
            mode={data?.ownerPhoneNumber ? 'change' : 'register'}
            onClose={() => setIsPhoneModalOpen(false)}
            onUpdated={refreshOwnerInfo}
          />
        </>
      ) : null}
    </div>
  )
}
